/**
 * CollabsView
 *
 * Orchestrator component for the collaborators management page.
 * Composes the top menu bar, data tables (collabs, warnings, special cases,
 * employment promises), and all associated modals/dialogs.
 */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useGridApiRef } from "@mui/x-data-grid";
import moment from "moment";
import styles from "./CollabsView.module.css";
import Langs, { Localize } from "../../app/lang/langs";
import { checkPermission } from "../../app/utils/helpers";
import { showSuccessToast, showErrorToast } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import { Dialog } from "primereact/dialog";
import { DynamicButton } from "../../components/buttons/DynamicButton";
import ExcelManager from "../../app/managers/excelManager";
import { setIsLoading } from "../common/commonSlice";
import { getSubcontractors } from "../../components/clients/clientsSlice";
import { getAllTeamsManagers, setSelectedTeams } from "../../components/teams/teamsSlice";
import { getManagersProfiles } from "../../components/permissions/permissionsSlice";
import {
  getAllCollabs,
  setCollabModalData,
  setUpdatePermissionsRes,
  getAllWarnings,
  setSelectedView,
  setAddWarningRes,
  setWarningData,
  setWarningDetailsDialog,
  deleteWarning,
  setWarningDeleteRes,
  setWarningEditRes,
  setAddWarningDialog,
  getSpecialCases,
  setSpecialCaseModalData,
  setDisplaySpecialCaseModalDetails,
  getAllContracts,
  removeCollabPhoto,
  setCollabPhoto
} from "../../components/collabs/collabsSlice";

// Sub-components
import CollabsTopMenu from "./CollabsTopMenu";
import CollabsDataTable from "./CollabsDataTable";
import { ProfilePictureEditor } from "../../components/collabs/ProfilePictureEditor";
import NetworkManager from "../../app/managers/networkManager";
import { usePhotoPrefetch } from "../../components/collabs/usePhotoPrefetch";
import WarningsDataTable from "./WarningsDataTable";
import SpecialCasesDataTable from "./SpecialCasesDataTable";
import EmploymentPromisesView from "./EmploymentPromisesView";

// Mobile sub-components
import MobileCollabsTopMenu from "./mobile/MobileCollabsTopMenu";
import MobileCollabsList from "./mobile/MobileCollabsList";
import MobileWarningsList from "./mobile/MobileWarningsList";
import MobileSpecialCasesList from "./mobile/MobileSpecialCasesList";
import MobileEmploymentPromisesList from "./mobile/MobileEmploymentPromisesList";

// Modals & Dialogs
import CollabModalDetails from "./CollabModalDetails";
import MobileCollabModalDetails from "./mobile/MobileCollabModalDetails";
import ResetPasswordDialog from "./ResetPasswordDialog";
import WarningDetailsDialog from "../warnings/dialogs/WarningDetails";
import AddExterneModal from "./AddExterneModal";
import MobileAddExterneModal from "./mobile/MobileAddExterneModal";
import AddWarning from "../warnings/dialogs/AddWarning";
import MergeCollabModal from "./MergeCollabModal";
import AddSpecialCaseModal from "./AddSpecialCaseModal";
import SpecialCaseDetailsDialog from "./SpecialCaseDetails";
import MobileAddSpecialCaseModal from "./mobile/MobileAddSpecialCaseModal";
import MobileSpecialCaseDetailsDialog from "./mobile/MobileSpecialCaseDetailsDialog";
import MobileAddWarning from "./mobile/MobileAddWarning";
import MobileWarningDetailsDialog from "./mobile/MobileWarningDetailsDialog";

export function CollabsView() {
  const dispatch = useDispatch();
  const urlParams = useParams();
  const navigate = useNavigate();
  const mounted = useRef(null);
  const dtCollabsApi = useGridApiRef();
  const dtWarningsApi = useGridApiRef();
  const dtSpecialCasesApi = useGridApiRef();
  const dtEmploymentPromisesApi = useGridApiRef();

  // Redux state
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const managersProfiles = useSelector((state) => state.permissions.managersProfiles);
  const permissionsMessage = useSelector((state) => state.collabs.updatePermissionsRes);
  const allCollabsList = useSelector((state) => state.collabs.allCollabsList);

  // Warms the row avatars into the browser cache in background chunks, so scrolling the
  // grid does not land on faces that are still fetching.
  usePhotoPrefetch(allCollabsList);
  const allWarningsList = useSelector((state) => state.collabs.warningsList);
  const deleteWarningRes = useSelector((state) => state.collabs.warningDeleteRes);
  const editWarningsRes = useSelector((state) => state.collabs.warningEditRes);
  const addWarningRes = useSelector((state) => state.collabs.addWarningRes);
  const selectedView = useSelector((state) => state.collabs.selectedView);
  const permissions = useSelector((state) => state.login.permissions);
  const isMobile = useSelector((state) => state.settings.isMobile);
  const lang = useSelector((state) => state.settings.lang);
  const specialCases = useSelector((state) => state.collabs.specialCases);
  const allContractsList = useSelector((state) => state.collabs.allContracts);
  const employmentPromisesList = useSelector((state) => state.employmentPromises.list);

  // Local state
  const [deleteWarningId, setDeleteWarningId] = useState(null);
  const [displayAddExterneModal, setDisplayAddExterneModal] = useState(false);
  const [displaySpecialCaseModal, setDisplaySpecialCaseModal] = useState(false);
  const [displayAddEmploymentPromiseModal, setDisplayAddEmploymentPromiseModal] = useState(false);
  const [mergeCollab, setMergeCollab] = useState(null);
  const [confirmDeleteVisibility, setConfirmDeleteVisibility] = useState(false);
  // The collab row whose picture is pending removal, or null. Holds the whole row rather
  // than an id so the dialog can name the person being acted on.
  const [photoToRemove, setPhotoToRemove] = useState(null);
  // The collab whose picture the editor is currently open for. Holds the whole row, not
  // just the id, so the dialog can name the person without looking them back up.
  const [photoToSet, setPhotoToSet] = useState(null);
  // Shared with the own-picture editor in MenuBar — only one photo write is in flight at a
  // time, so a single pair of flags covers both.
  const photoSaving = useSelector((state) => state.collabs.myPhotoSaving);
  const photoError = useSelector((state) => state.collabs.myPhotoError);
  const [filteredCollabs, setFilteredCollabs] = useState(null);

  const hasCollabsView = checkPermission(config.scopes.collabs, config.permissionTypes.view);
  const hasWarningsViewLocal = checkPermission(config.scopes.warnings, config.permissionTypes.view);
  const viewsOptions = [
    { label: <Langs str="Collabs" />, value: 0 },
    ...(hasWarningsViewLocal ? [{ label: <Langs str="Suivis" />, value: 1 }] : []),
    ...(hasCollabsView ? [
      { label: <Langs str="SPECIAL_CASES" />, value: 2 },
      { label: <Langs str="EMPLOYMENT_PROMISES" />, value: 3 },
    ] : []),
  ];

  // -----------------------------------------------------------------------
  // Effects
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (checkPermission(config.scopes.collabs, config.permissionTypes.view)) {
      dispatch(setSelectedView(0));
    } else if (
      checkPermission(config.scopes.warnings, config.permissionTypes.view) &&
      !checkPermission(config.scopes.collabs, config.permissionTypes.view)
    ) {
      dispatch(setSelectedView(1));
    }
  }, [mounted, permissions]);

  useEffect(() => {
    if (urlParams.subView && viewsOptions.find(v => v.value == urlParams.subView)) {
      dispatch(setSelectedView(Number(urlParams.subView)));
    }
  }, [urlParams]);

  useEffect(() => {
    if (!mounted.current) {
      dispatch(getSubcontractors());
      if (checkPermission(config.scopes.collabs, config.permissionTypes.view)) {
        dispatch(getSpecialCases());
      }
      if (checkPermission(config.scopes.collabs, config.permissionTypes.view)) {
        dispatch(getAllCollabs());
        dispatch(getAllContracts());
        // The Excel export adds one column per manager profile (see exportExcelCollabs), and
        // this screen never fetched the list — it rendered whatever a previously visited
        // screen happened to leave in the store, so on a cold load those columns came out
        // empty. `{}` rather than `{ all: true }`: same curated list the teams table uses, so
        // a profile hidden from the columns there is hidden from this export too.
        dispatch(getManagersProfiles({}));
      }
      if (checkPermission(config.scopes.warnings, config.permissionTypes.view)) {
        dispatch(getAllWarnings());
      }
      mounted.current = true;
    }
  }, [dispatch]);

  useEffect(() => {
    toastRes();
  }, [permissionsMessage]);

  useEffect(() => {
    if (deleteWarningRes != null) {
      if (deleteWarningRes.trim().toLowerCase().includes("successfully")) {
        showSuccessToast(deleteWarningRes);
      } else {
        showErrorToast(deleteWarningRes);
      }
      dispatch(setWarningDeleteRes(null));
    }
  }, [deleteWarningRes]);

  useEffect(() => {
    if (editWarningsRes != null) {
      if (editWarningsRes.trim().toLowerCase().includes("successfully")) {
        showSuccessToast(editWarningsRes);
      } else {
        showErrorToast(editWarningsRes);
      }
      dispatch(setWarningEditRes(null));
    }
  }, [editWarningsRes]);

  useEffect(() => {
    if (addWarningRes != null) {
      if (addWarningRes.trim().toLowerCase().includes("successfully")) {
        showSuccessToast(<Langs str="WARNING_SUCCESS" />);
      } else {
        showErrorToast(<Langs str="DUPICATE_WARNING" />);
      }
      dispatch(setAddWarningRes(null));
    }
  }, [addWarningRes]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  function toastRes() {
    if (permissionsMessage != null) {
      if (permissionsMessage.trim().toLowerCase().includes("successfully")) {
        showSuccessToast(permissionsMessage);
      } else {
        showErrorToast(permissionsMessage);
      }
      dispatch(setUpdatePermissionsRes());
    }
  }

  function viewCollabModal(data) {
    if (!checkPermission(config.scopes.collabs, config.permissionTypes.edit)) return;
    const modalData = { ...data, show: true };
    dispatch(setCollabModalData(modalData));
  }

  function viewCollabPlanning(data) {
    if (data.current_id_team) {
      dispatch(setSelectedTeams([data.current_id_team]));
      navigate(
        `/planning/${data.current_id_team}/MONTHLY${moment().startOf("month").format("DDMMYYYY")}`
      );
    }
  }

  function handleCollabFusion(data) {
    setMergeCollab(data);
  }

  function viewWarningModal(data) {
    dispatch(setWarningData(data));
    dispatch(setWarningDetailsDialog(true));
  }

  function viewSpecialCaseModal(data) {
    dispatch(setSpecialCaseModalData(data));
    dispatch(setDisplaySpecialCaseModalDetails(true));
  }

  const HandleAddWarningModal = () => {
    dispatch(setAddWarningDialog(true));
  };

  const handleEndOfMerge = (collab) => {
    if (collab) viewCollabModal(collab);
    dispatch(getAllCollabs());
  };

  const showConfirmRemovePhoto = (rowData) => {
    setPhotoToRemove(rowData);
  };

  const showSetPhotoEditor = (rowData) => {
    setPhotoToSet(rowData);
  };

  function setPhotoFinal(dataUrl) {
    if (!photoToSet) return;
    // Closed on success only. The thunk swallows transport errors into the payload rather
    // than rejecting, so a failed upload would otherwise close the dialog and report
    // nothing — the editor is the only place `myPhotoError` is shown.
    dispatch(setCollabPhoto({ idCollab: photoToSet.id, image: dataUrl })).then((action) => {
      const data = action.payload && action.payload.responseData;
      if (data && data.status == 1) setPhotoToSet(null);
    });
  }

  function removePhotoFinal() {
    if (photoToRemove) dispatch(removeCollabPhoto(photoToRemove.id));
    setPhotoToRemove(null);
  }

  const showConfirmDelete = (id) => {
    setDeleteWarningId(id);
    setConfirmDeleteVisibility(true);
  };

  function deleteWarningFinal() {
    dispatch(deleteWarning(deleteWarningId));
    setDeleteWarningId(null);
    setConfirmDeleteVisibility(false);
  }

  const handleViewChange = (view) => {
    if (view != null) {
      let selectedTeamId = selectedTeamsIds[0];
      navigate(`/collabs/${selectedTeamId}/${view}`);
    }
  };

  // -----------------------------------------------------------------------
  // Export
  // -----------------------------------------------------------------------

  const exportExcelCollabs = (allManagers) => {
    let filteredData = filteredCollabs || allCollabsList;

    let exportData = filteredData.reduce((array, current) => {
      let teamManagers = allManagers[current.current_id_team];
      let managersByProfile = {};
      if (teamManagers) {
        teamManagers.map((tm) => {
          if (!managersByProfile[tm.type]) managersByProfile[tm.type] = [];
          managersByProfile[tm.type].push(`${tm.prenom} ${tm.nom}`);
        });
      }
      let collabData = {
        "Matricule": current.matricule || "",
        "Entreprise": current.company || "",
        "Type de collaborateur": current.type_collab || "",
        "Civilité": current.title || "",
        "Nom de famille": current.nom || "",
        "Prénom": current.prenom || "",
        "Date de Naissance": current.date_naissance || "",
        "Statut": current.statut || "",
        "Statut Silae": Localize(current.silaeStatus) || "",
        "Équipe actuelle": current.current_team_name || "",
        "Dernière équipe": current.last_team_name || "",
        "Téléphone": current.mobileNumber || "",
        "Type de contrat": current.type_contrat || "",
        "Email": current.email || "",
        "Numéro de sécurité sociale": current.security_number || "",
        "Poste actuel": current.current_poste || "",
        "Nature emploi": current.nature_emploi || "",
        "Statut emploi": current.statut_emploi || "",
        "Coefficient": current.coefficient || "",
        "Position": current.position || "",
        "Date de première expérience": current.first_experience_date ? moment(current.first_experience_date).format("DD-MM-YYYY") : "",
        "Date d'ancienneté": current.ancienneteeDate ? moment(current.ancienneteeDate).format("DD-MM-YYYY") : "",
        "Date d'entrée": current.entryDate ? moment(current.entryDate).format("DD-MM-YYYY") : "",
        "Période d'essai 1": current.periodeEssai1 ? moment(current.periodeEssai1).format("DD-MM-YYYY") : "",
        "Période d'essai 2": current.periodeEssai2 ? moment(current.periodeEssai2).format("DD-MM-YYYY") : "",
        "Date de fin titre de sejour": current.date_fin_titre_sejour ? moment(current.date_fin_titre_sejour).format("DD-MM-YYYY") : "",
        "Date de sortie": current.exitDate ? moment(current.exitDate).format("DD-MM-YYYY") : "",
        "Date de sortie effective": current.last_contract_sortie_date ? moment(current.last_contract_sortie_date).format("DD-MM-YYYY") : "",
        "Motif de sortie": current.motif_sortie || "",
        "Commentaires": current.comments || "",
        "CPN-1 (restant dans Silae)": current.solde_cp_old_remaining || "",
        "CPN (restant dans Silae)": current.solde_cp_current_remaining || "",
        "CPN-1": current.solde_cp_old || "",
        "Date d'expiration CPN-1": current.solde_cp_old_expiry ? moment(current.solde_cp_old_expiry).format("DD-MM-YYYY") : "",
        "CPN": current.solde_cp_current || "",
        "Frais téléphone": current.frais_tel || "",
        "Forfait déplacement": current.forfait_deplacement || "",
        "Date début CP": current.date_debut_cp ? moment(current.date_debut_cp).format("DD-MM-YYYY") : "",
        "Date fin CP": current.date_fin_cp ? moment(current.date_fin_cp).format("DD-MM-YYYY") : "",
        "Nouvelle date fin CP": current.new_date_fin_cp ? moment(current.new_date_fin_cp).format("DD-MM-YYYY") : "",
        "TJM achat": current.tjm_achat || "",
        "TJM vente": current.tjm_vente || "",
        "Sous-traitant": current.subcontractor || "",
        "Email sous-traitant": current.subcontractor_email || "",
        "Date de création": current.createdAt ? moment(current.createdAt).format("DD-MM-YYYY") : "",
        "Prime de mission": current.prime_mission ?? "",
      };
      managersProfiles.forEach(profile => {
        collabData[profile.name] = (managersByProfile[profile.name] || []).join(", ");
      });

      array.push(collabData);
      return array;
    }, []);

    ExcelManager.getInstance().generateAndDownloadExcelFromData(exportData, "collaborateurs");
  };

  const exportExcelContracts = () => {
    let exportDataContract = allContractsList.reduce((array, current) => {
      let contractData = {
        "Prénom": current.prenom || "",
        "Nom de famille": current.nom || "",
        "ID": current.id || "",
        "ID Collaborateur": current.id_collab || "",
        "ID Entreprise": current.id_company || "",
        "ID Sous-traitant": current.id_subcontractor || "",
        "Type de contrat": current.type_contrat || "",
        "Type de collaborateur": current.type_collab || "",
        "Statut emploi": current.statut_emploi || "",
        "Date d'entrée": current.entree_date ? moment(current.entree_date).format("DD-MM-YYYY") : "",
        "Date de sortie": current.sortie_date ? moment(current.sortie_date).format("DD-MM-YYYY") : "",
        "Poste": current.position || "",
        "Coefficient": current.coefficient || "",
        "Code CSP": current.code_csp || "",
        "Période d'essai 1": current.periode_essai_1 ? moment(current.periode_essai_1).format("DD-MM-YYYY") : "",
        "Période d'essai 2": current.periode_essai_2 ? moment(current.periode_essai_2).format("DD-MM-YYYY") : "",
        "Mise sous pli": current.mise_sous_pli || "",
        "Dédit formation": current.dedit_formation || "",
        "Motif de sortie": current.motif_sortie || "",
        "Durée période d'essai 1": current.duree_periode_essai_1 || "",
        "Durée période d'essai 2": current.duree_periode_essai_2 || "",
        "Date rappel 1": current.date_rappel_1 ? moment(current.date_rappel_1).format("DD-MM-YYYY") : "",
        "Date rappel 2": current.date_rappel_2 ? moment(current.date_rappel_2).format("DD-MM-YYYY") : "",
        "N'a pas démarré": current.did_not_start || "",
        "TJM achat": current.tjm_achat || "",
        "TJM vente": current.tjm_vente || "",
        "Date début CP": current.date_debut_cp ? moment(current.date_debut_cp).format("DD-MM-YYYY") : "",
        "Date fin CP": current.date_fin_cp ? moment(current.date_fin_cp).format("DD-MM-YYYY") : "",
        "Nouvelle date début CP": current.new_date_debut_cp ? moment(current.new_date_debut_cp).format("DD-MM-YYYY") : "",
        "Nouvelle date fin CP": current.new_date_fin_cp ? moment(current.new_date_fin_cp).format("DD-MM-YYYY") : "",
        "Date d'envoi": current.sent_date ? moment(current.sent_date).format("DD-MM-YYYY") : "",
        "Mutuelle": current.mutuelle || "",
        "Ticket restaurant": current.ticket_restaurant || "",
        "Pass Navigo": current.pass_navigo || "",
        "Taux horaire": current.taux_horaire || "",
        "Établissement": current.etablissement || "",
        "Nature emploi": current.nature_emploi || "",
        "Renouvellement période d'essai": current.renew_periode_essai || "",
        "Responsable contractuel": current.responsable_contractuel || "",
        "Statut Silae": current.silae_status || "",
        "Date de création": current.created_at ? moment(current.created_at).format("DD-MM-YYYY") : "",
        "Date de mise à jour": current.updated_at ? moment(current.updated_at).format("DD-MM-YYYY") : "",
        "Créé par": current.created_by || "",
        "Mis à jour par": current.updated_by || "",
        "Nom sous-traitant": current.subcontractor_name || "",
        "Tuteur": current.tutor_name || "",
        "Ecole": current.school || "",
        "Contact école": current.school_contact || "",
        "Diplome": current.diploma || "",
        "Coût de la formation": current.training_cost || "",
        "Reste à charge": current.remaining_cost || "",
        "Commentaires": current.comments || "",
      };

      array.push(contractData);
      return array;
    }, []);
    console.log(exportDataContract);
    ExcelManager.getInstance().generateAndDownloadExcelFromData(exportDataContract, "contrats_collaborateurs");
  };

  const exportFilteredData = () => {
    let subView = viewsOptions.find(v => v.value === selectedView);
    if (!subView) return;
    switch (subView.value) {
      case 0:
        dispatch(setIsLoading(true));
        dispatch(getAllTeamsManagers())
          .unwrap()
          .then((response) => {
            exportExcelCollabs(response?.responseData?.managers);
            dispatch(setIsLoading(false));
          })
          .catch((error) => {
            dispatch(setIsLoading(false));
          });
        break;
      case 1: {
        const L = (key) => Localize(key, lang);
        const warningsExport = (allWarningsList || []).map((w) => ({
          [L("ID")]: w.id || "",
          [L("Matricule")]: w.collab_matricule || "",
          [L("Family Name")]: w.collab_nom || "",
          [L("Name")]: w.collab_prenom || "",
          [L("category")]: Localize(w.category, lang) || "",
          [L("statut")]: Localize(w.status, lang) || "",
          [L("Client")]: w.team_name || "",
          [L("warning_date")]: w.warning_date ? moment(w.warning_date).format("DD-MM-YYYY") : "",
          [L("Comments")]: w.comments || "",
        }));
        ExcelManager.getInstance().generateAndDownloadExcelFromData(warningsExport, "suivis");
        break;
      }
      case 2: {
        const L = (key) => Localize(key, lang);
        const scExport = (specialCases || []).map((sc) => ({
          [L("ID")]: sc.id || "",
          [L("Family Name")]: sc.nom_collab || "",
          [L("Name")]: sc.prenom_collab || "",
          [L("category")]: Localize(sc.case, lang) || "",
          [L("Client")]: sc.team_name || "",
          [L("START_DATE")]: sc.startDate ? moment(sc.startDate).format("DD-MM-YYYY") : "",
          [L("END_DATE")]: sc.endDate ? moment(sc.endDate).format("DD-MM-YYYY") : "",
          [L("Comments")]: sc.comments || "",
        }));
        ExcelManager.getInstance().generateAndDownloadExcelFromData(scExport, "cas_speciaux");
        break;
      }
      case 3: {
        const L = (key) => Localize(key, lang);
        const epExport = (employmentPromisesList || []).map((ep) => ({
          [L("ID")]: ep.id || "",
          [L("Company")]: ep.company_name || "",
          [L("Family Name")]: ep.family_name || "",
          [L("Name")]: ep.name || "",
          [L("EMAIL")]: ep.email || "",
          [L("Progression")]: ep.progression || "",
          [L("STATUS")]: ep.status || "",
          [L("REQUEST_DATE")]: ep.request_date ? moment(ep.request_date).format("DD-MM-YYYY") : "",
          [L("SENT_DATE")]: ep.sent_date ? moment(ep.sent_date).format("DD-MM-YYYY") : "",
          [L("START_DATE")]: ep.start_date ? moment(ep.start_date).format("DD-MM-YYYY") : "",
        }));
        ExcelManager.getInstance().generateAndDownloadExcelFromData(epExport, "promesses_embauche");
        break;
      }
      default:
        break;
    }
  };

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  function getScrollHeight() {
    let scrollHeight = "calc(100vh - 160px)";
    switch (isMobile) {
      case 2:
        scrollHeight = "calc(100vh - 220px)";
        break;
      case 3:
        scrollHeight = "calc(100vh - 160px)";
        break;
      default:
        break;
    }
    return scrollHeight;
  }

  function renderBody() {
    let scrollHeight = getScrollHeight();
    let subView = viewsOptions.find(v => v.value === selectedView);
    if (!subView) return null;

    // Mobile layout (isMobile === 2): card-based lists
    if (isMobile === 2) {
      switch (subView.value) {
        case 0:
          return (
            <MobileCollabsList
              apiRef={dtCollabsApi}
              data={allCollabsList}
              allCollabsList={allCollabsList}
              scrollHeight={scrollHeight}
              onRowDoubleClick={viewCollabModal}
              onFusion={handleCollabFusion}
              onViewPlanning={viewCollabPlanning}
              onFilteredDataChange={setFilteredCollabs}
            />
          );
        case 1:
          return (
            <MobileWarningsList
              apiRef={dtWarningsApi}
              data={allWarningsList}
              scrollHeight={scrollHeight}
              onViewDetails={viewWarningModal}
              onDelete={showConfirmDelete}
            />
          );
        case 2:
          return (
            <MobileSpecialCasesList
              apiRef={dtSpecialCasesApi}
              data={specialCases}
              scrollHeight={scrollHeight}
              onViewDetails={viewSpecialCaseModal}
            />
          );
        case 3:
          return <MobileEmploymentPromisesList
            addVisible={displayAddEmploymentPromiseModal}
            setAddVisible={setDisplayAddEmploymentPromiseModal}
          />;
        default:
          return null;
      }
    }

    // Desktop / Tablet layout: DataGrid tables
    switch (subView.value) {
      case 0:
        return (
          <CollabsDataTable
            apiRef={dtCollabsApi}
            data={allCollabsList}
            allCollabsList={allCollabsList}
            scrollHeight={scrollHeight}
            onRowDoubleClick={viewCollabModal}
            onFusion={handleCollabFusion}
            onViewPlanning={viewCollabPlanning}
            onFilteredDataChange={setFilteredCollabs}
            onSetPhoto={showSetPhotoEditor}
            onRemovePhoto={showConfirmRemovePhoto}
          />
        );
      case 1:
        return (
          <WarningsDataTable
            apiRef={dtWarningsApi}
            data={allWarningsList}
            scrollHeight={scrollHeight}
            onViewDetails={viewWarningModal}
            onDelete={showConfirmDelete}
          />
        );
      case 2:
        return (
          <SpecialCasesDataTable
            apiRef={dtSpecialCasesApi}
            data={specialCases}
            scrollHeight={scrollHeight}
            onViewDetails={viewSpecialCaseModal}
          />
        );
      case 3:
        return <EmploymentPromisesView
          apiRef={dtEmploymentPromisesApi}
          addVisible={displayAddEmploymentPromiseModal}
          setAddVisible={setDisplayAddEmploymentPromiseModal}
        />;
      default:
        return null;
    }
  }

  function renderSetPhotoEditor() {
    if (!photoToSet) return null;
    const name = `${photoToSet.prenom || ""} ${photoToSet.nom || ""}`.trim();
    return (
      <ProfilePictureEditor
        open={!!photoToSet}
        currentPhotoUrl={NetworkManager.getInstance().getCollabPhotoUrl(photoToSet.id, photoToSet.photoUpdatedAt)}
        title={Localize("PHOTO_SET_FOR_COLLAB_TITLE").replace("%NAME%", name)}
        saving={photoSaving}
        error={photoError}
        onClose={() => setPhotoToSet(null)}
        onSave={(dataUrl) => setPhotoFinal(dataUrl)}
      />
    );
  }

  function renderConfirmRemovePhoto() {
    const name = photoToRemove
      ? `${photoToRemove.prenom || ""} ${photoToRemove.nom || ""}`.trim()
      : "";
    return (
      <Dialog
        header={<Langs str="PHOTO_REMOVE" />}
        draggable={false}
        visible={!!photoToRemove}
        style={isMobile == 1 ? { width: "25vw" } : { width: "92vw", maxWidth: "480px" }}
        onHide={() => setPhotoToRemove(null)}
      >
        <label className="mb-3 text-center w-100">
          <Langs str="PHOTO_REMOVE_CONFIRM" params={{ NAME: name }} />
        </label>
        <div className={`${isMobile === 2 ? "w-100" : "w-50"} mx-auto`}>
          <DynamicButton
            onClick={() => removePhotoFinal()}
            title={<Langs str="PHOTO_REMOVE" />}
            active={true}
            color="red"
          />
        </div>
      </Dialog>
    );
  }

  function renderConfirmDelete() {
    return (
      <div className="card flex justify-content-center">
        <Dialog
          header={<Langs str="DELETE" />}
          draggable={false}
          visible={confirmDeleteVisibility}
          style={isMobile == 1 ? { width: "25vw" } : { width: "92vw", maxWidth: "480px" }}
          onHide={() => {
            setConfirmDeleteVisibility(false);
            setDeleteWarningId(null);
          }}
        >
          <label className="mb-3 text-center w-100">
            <Langs str="CONFIRM_DELETE" />
          </label>
          <div className={`${isMobile === 2 ? "w-100" : "w-50"} mx-auto`}>
            <DynamicButton
              onClick={() => deleteWarningFinal()}
              title={<Langs str="DELETE_WARNING" />}
              active={true}
              color="red"
            />
          </div>
        </Dialog>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------

  return (
    <div className={styles.containerView}>
      <div className={`topMenu ${isMobile !== 2 ? styles.collabsTopMenuWrapper : ""}`}>
        {isMobile === 2 ? (
          <MobileCollabsTopMenu
            isMobile={isMobile}
            viewsOptions={viewsOptions}
            selectedView={selectedView}
            onViewChange={handleViewChange}
            onExport={exportFilteredData}
            onAddExterne={() => setDisplayAddExterneModal(true)}
            onAddWarning={HandleAddWarningModal}
            onAddSpecialCase={() => setDisplaySpecialCaseModal(true)}
            onAddEmploymentPromise={() => setDisplayAddEmploymentPromiseModal(true)}
          />
        ) : (
          <CollabsTopMenu
            isMobile={isMobile}
            viewsOptions={viewsOptions}
            selectedView={selectedView}
            onExport={exportFilteredData}
            onAddExterne={() => setDisplayAddExterneModal(true)}
            onAddWarning={HandleAddWarningModal}
            onAddSpecialCase={() => setDisplaySpecialCaseModal(true)}
            onAddEmploymentPromise={() => setDisplayAddEmploymentPromiseModal(true)}
          />
        )}
      </div>
      {renderBody()}
      {renderConfirmDelete()}
      {renderConfirmRemovePhoto()}
      {renderSetPhotoEditor()}
      {isMobile === 2 ? (
        <MobileCollabModalDetails
          page="collabs"
          tabs={["contrat", "societe", "mission", "donneesPerso", "profil"]}
        />
      ) : (
        <CollabModalDetails
          page="collabs"
          tabs={["contrat", "societe", "mission", "donneesPerso", "profil"]}
        />
      )}
      <ResetPasswordDialog />
      {isMobile === 2 ? (
        <>
          <MobileWarningDetailsDialog />
          <MobileAddWarning />
        </>
      ) : (
        <>
          <WarningDetailsDialog />
          <AddWarning />
        </>
      )}
      {isMobile === 2 ? (
        <MobileAddExterneModal
          visible={displayAddExterneModal}
          onHide={() => setDisplayAddExterneModal(false)}
        />
      ) : (
        <AddExterneModal
          visible={displayAddExterneModal}
          onHide={() => setDisplayAddExterneModal(false)}
        />
      )}
      <MergeCollabModal
        visible={mergeCollab}
        originalCollab={mergeCollab}
        onMergeDone={(collab) => handleEndOfMerge(collab)}
        onHide={() => setMergeCollab(null)}
      />
      {isMobile === 2 ? (
        <>
          <MobileAddSpecialCaseModal
            visible={displaySpecialCaseModal}
            onHide={() => setDisplaySpecialCaseModal(false)}
          />
          <MobileSpecialCaseDetailsDialog />
        </>
      ) : (
        <>
          <AddSpecialCaseModal
            visible={displaySpecialCaseModal}
            onHide={() => setDisplaySpecialCaseModal(false)}
          />
          <SpecialCaseDetailsDialog />
        </>
      )}
    </div>
  );
}
